import { useQuery } from '@tanstack/react-query';
import queryString from 'query-string';
import { api } from '@xtnd/web/shared/api';
import { UserRole } from '@repo/types';
import type { SelectOption } from '@repo/ui';
import { useMemo } from 'react';

interface UserResponse {
  userId: string;
  email: string;
  name: string;
  roles: string[];
  isActive: boolean;
  attyCode?: string;
}

export interface DelegateUserOption extends SelectOption {
  email: string;
  name: string;
  attyCode?: string;
}

interface UserListResponse {
  data: UserResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Hook to fetch users with CLERK role for the Appeals Clerk assignment filter
 * Uses the convenience endpoint /users/clerks
 * @returns React Query result with clerk users formatted as SelectOption[] with colors
 */
export function useAssignedUsers() {
  const query = queryString.stringify({
    roles: [
      UserRole.CLERK,
      UserRole.ADMIN,
      UserRole.ACCOUNTING_ADMIN,
      UserRole.BILLING_ADMIN,
      UserRole.SUPERVISOR,
    ],
    isActive: true,
  });

  return useQuery({
    queryKey: ['users', 'clerks'],
    queryFn: async (): Promise<SelectOption[]> => {
      const response = await api.get<UserListResponse>(`/users?${query}`);

      // Added 'index' as the second argument to the map function
      return (
        response.data?.map(user => {
          return {
            label: user.name || user.email,
            value: user.email,
          };
        }) || []
      );
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch all active users for the delegate user selection
 * @returns React Query result with all active users formatted as SelectOption[]
 */
export function useDelegateUsers() {
  const query = queryString.stringify({
    roles: [UserRole.PARTNER, UserRole.ASSISTANT, UserRole.ASSOCIATE],
    isActive: true,
  });

  return useQuery({
    queryKey: ['users', 'delegates'],
    queryFn: async (): Promise<DelegateUserOption[]> => {
      const response = await api.get<UserListResponse>(`/users?${query}`);

      return (
        response.data?.map(user => {
          return {
            email: user.email,
            name: user.name,
            attyCode: user.attyCode,
            value: user.email,
            label: `${user.name} (${user.email})`,
          };
        }) || []
      );
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to get clerk users with maps for efficient lookups.
 * Encapsulates the common pattern of fetching clerk users and creating lookup maps.
 *
 * @returns Object containing:
 *  - clerkUsersOptions: SelectOption[] for dropdowns (with colors)
 *  - clerkUserMap: Map<string, string> for email-to-name lookup
 *  - clerkColorMap: Map<string, ClerkColorInfo> for email-to-color lookup
 *  - isLoading: boolean
 *
 * @example
 * const { clerkUserMap, clerkColorMap, clerkUsersOptions, isLoading } = useClerkUserMap();
 * const clerkName = clerkUserMap.get(email) ?? '-';
 * const color = clerkColorMap.get(email);
 */
export function useClerkUserMap() {
  const { data: clerkUsersOptions = [], isLoading } = useAssignedUsers();

  const clerkUserMap = useMemo(
    () => new Map(clerkUsersOptions.map(user => [user.value, user.label])),
    [clerkUsersOptions]
  );

  return {
    clerkUsersOptions,
    clerkUserMap,
    isLoading,
  };
}
